import { memo, type ElementType, type ReactNode } from "react";
import { styled } from "../../../../lib/styled";

export interface ShieldBadgeProps {
  /** Left (key) segment — dark fill, bold. */
  label: ReactNode;
  /** Right (value) segment — lighter fill. Omit for a single-segment badge. */
  value?: ReactNode;
  /** Override the label segment background colour. Defaults to grey[600]. */
  labelColor?: string;
  /** Optional icon rendered before the label text, sized and aligned by the badge. */
  icon?: ElementType;
}

const BadgeRoot = styled("span")({
  display: "inline-flex",
  alignItems: "stretch",
  borderRadius: 3,
  overflow: "hidden",
  fontSize: "0.75rem",
  lineHeight: 1.7,
  whiteSpace: "nowrap",
});

const Segment = styled("span")(({ theme }) => ({
  paddingInline: theme.spacing(0.75),
}));

const LabelSegment = styled(Segment)<{ $color?: string }>(({ theme, $color }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  backgroundColor: $color ?? theme.palette.grey[600],
  color: theme.palette.common.white,
  fontWeight: 600,
  "& svg": { fontSize: "1em", flexShrink: 0 },
}));

const ValueSegment = styled(Segment)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[200],
  color: theme.palette.text.primary,
}));

/** shields.io-style two-segment badge: dark label on the left, lighter value on the right.
 *  Omit value for a single-segment label-only badge. Pass icon for an inline icon before label. */
export const ShieldBadge = memo(function ShieldBadge({
  label,
  value,
  labelColor,
  icon: Icon,
}: ShieldBadgeProps) {
  return (
    <BadgeRoot>
      <LabelSegment $color={labelColor}>
        {Icon && <Icon fontSize="inherit" />}
        {label}
      </LabelSegment>
      {value !== undefined && <ValueSegment>{value}</ValueSegment>}
    </BadgeRoot>
  );
});
